import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { DEFAULT_IMAGE, SITE_ORIGIN, normalizePath, resolveRouteSEO } from "../seo/routeSeo";

const SEOContext = createContext(null);

function updateMeta(attribute, key, value) {
  const selector = `meta[${attribute}="${key}"]`;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", value);
}

function applyPageMetadata(pathname, seo) {
  const fallbackPath = normalizePath(pathname);
  const canonicalPath =
    typeof seo.canonicalPath === "string" && seo.canonicalPath.startsWith("/")
      ? normalizePath(seo.canonicalPath)
      : fallbackPath;
  const canonicalURL = new URL(canonicalPath, SITE_ORIGIN).href;
  const title = seo.title || "Anjaan Vivek | Contemporary Artist";
  const description = seo.description || "Original artworks and pictorial biographies by Anjaan Vivek.";
  const image = seo.image || DEFAULT_IMAGE;
  const imageAlt = seo.imageAlt || "Original artwork by Anjaan Vivek";

  document.title = title;
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }
  canonical.href = canonicalURL;

  updateMeta("name", "description", description);
  updateMeta("name", "robots", seo.noindex
    ? (seo.nofollow ? "noindex, nofollow" : "noindex, follow")
    : "index, follow, max-image-preview:large");
  updateMeta("property", "og:type", seo.type || "website");
  updateMeta("property", "og:url", canonicalURL);
  updateMeta("property", "og:site_name", "Anjaan Vivek");
  updateMeta("property", "og:title", title);
  updateMeta("property", "og:description", description);
  updateMeta("property", "og:image", image);
  updateMeta("property", "og:image:alt", imageAlt);
  updateMeta("name", "twitter:card", "summary_large_image");
  updateMeta("name", "twitter:title", title);
  updateMeta("name", "twitter:description", description);
  updateMeta("name", "twitter:image", image);
  updateMeta("name", "twitter:image:alt", imageAlt);

  const schemaId = "anjaan-page-jsonld";
  let schemaTag = document.getElementById(schemaId);
  if (seo.structuredData) {
    if (!schemaTag) {
      schemaTag = document.createElement("script");
      schemaTag.id = schemaId;
      schemaTag.type = "application/ld+json";
      document.head.appendChild(schemaTag);
    }
    // textContent ensures data isn't interpreted as markup.
    schemaTag.textContent = JSON.stringify(seo.structuredData).replace(/</g, "\\u003c");
  } else if (schemaTag) {
    schemaTag.remove();
  }
}

/**
 * Place <SEOProvider> directly inside <BrowserRouter>, wrapping <Routes>.
 * The default SEO is calculated from the URL, with no SEO component needed on
 * static pages. Detail pages can override after fetching the record.
 */
export function SEOProvider({ children }) {
  const location = useLocation();
  const [pageOverride, setPageOverride] = useState(null);
  const routeKey = location.key;

  const publishPageSEO = useCallback((next) => {
    setPageOverride((current) => {
      if (!next) {
        // Ignore unmount cleanups from the previous route.
        return current?.routeKey === routeKey ? null : current;
      }
      return { routeKey, data: next };
    });
  }, [routeKey]);

  const defaults = resolveRouteSEO(location.pathname);
  const override = pageOverride?.routeKey === routeKey ? pageOverride.data : null;
  const serialized = JSON.stringify({ ...defaults, ...(override || {}) });

  useEffect(() => {
    applyPageMetadata(location.pathname, JSON.parse(serialized));
  }, [location.pathname, serialized]);

  return <SEOContext.Provider value={publishPageSEO}>{children}</SEOContext.Provider>;
}

/**
 * Optional hook for pages with fetched content. Call unconditionally within
 * the component, passing null until its painting/print/series data is loaded.
 *
 * Example: usePageSEO(painting ? { title: `${painting.title} | Anjaan Vivek`,
 *                                   description: '...', image: '...' } : null);
 */
export function usePageSEO(metadata) {
  const publishPageSEO = useContext(SEOContext);
  if (!publishPageSEO) throw new Error("usePageSEO must be used inside SEOProvider");

  const serialized = metadata ? JSON.stringify(metadata) : "";
  useEffect(() => {
    if (!serialized) return undefined;
    publishPageSEO(JSON.parse(serialized));
    return () => publishPageSEO(null);
  }, [publishPageSEO, serialized]);
}
